// Subtle, randomised outdoor "gali" soundscape tied to the time of day.
// Mechanical sounds are synthesised via WebAudio; hawker calls use speech.
// Everything respects the shared ambient mute (the music button).
import ambient from "@/lib/ambientAudio";

let ac = null;
const ctx = () => {
  try {
    if (!ac) ac = new (window.AudioContext || window.webkitAudioContext)();
    if (ac.state === "suspended") ac.resume();
    return ac;
  } catch { return null; }
};
const on = () => { try { return !ambient.muted; } catch { return true; } };

function blip({ freq = 440, dur = 0.3, type = "sine", gain = 0.06, slideTo, delay = 0 }) {
  const c = ctx();
  if (!c || !on()) return;
  const t0 = c.currentTime + delay;
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = type;
  o.frequency.setValueAtTime(freq, t0);
  if (slideTo) o.frequency.exponentialRampToValueAtTime(Math.max(20, slideTo), t0 + dur);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(gain, t0 + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  o.connect(g); g.connect(c.destination);
  o.start(t0); o.stop(t0 + dur + 0.05);
}

const cycleBell = () => { blip({ freq: 1850, dur: 0.09, type: "square", gain: 0.05 }); blip({ freq: 1850, dur: 0.09, type: "square", gain: 0.045, delay: 0.15 }); };
const templeBell = () => { [784, 1176, 1568].forEach((f, i) => blip({ freq: f, dur: 1.6, type: "sine", gain: 0.045 / (i + 1) })); };
const dogBark = () => { blip({ freq: 300, dur: 0.12, type: "sawtooth", gain: 0.05, slideTo: 170 }); blip({ freq: 320, dur: 0.1, type: "sawtooth", gain: 0.045, slideTo: 190, delay: 0.2 }); };
const birds = () => { [2400, 2900, 2600, 3100].forEach((f, i) => blip({ freq: f, dur: 0.06, type: "triangle", gain: 0.035, delay: i * 0.1, slideTo: f + 500 })); };
const cooker = () => {
  const c = ctx();
  if (!c || !on()) return;
  const t0 = c.currentTime;
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = "sine";
  o.frequency.setValueAtTime(1500, t0);
  o.frequency.linearRampToValueAtTime(2450, t0 + 0.5);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(0.045, t0 + 0.12);
  g.gain.setValueAtTime(0.045, t0 + 0.85);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + 1.2);
  o.connect(g); g.connect(c.destination);
  o.start(t0); o.stop(t0 + 1.3);
};
const speak = (text, { pitch = 1, rate = 0.95 } = {}) => {
  try {
    if (window.speechSynthesis && on()) {
      const u = new SpeechSynthesisUtterance(text);
      u.lang = "hi-IN"; u.pitch = pitch; u.rate = rate; u.volume = 0.5;
      window.speechSynthesis.speak(u);
    }
  } catch { /* ignore */ }
};

// per time-of-day sound pools (each entry is a function)
const POOLS = {
  morning: [() => speak("Doodh! Doodh le lo!"), () => speak("Paper! Akhbaar!"), templeBell, birds, cycleBell, birds],
  afternoon: [cooker, () => speak("Sabzi le lo! Aloo pyaaz!"), () => speak("Raddi paper wala!"), cycleBell, cooker],
  evening: [() => speak("Arre bachcho, ghar aao!"), () => speak("Chaat! Gol gappe!"), cycleBell, () => speak("Sabzi le lo!"), templeBell, dogBark],
  night: [dogBark, () => speak("Chowkidaar… jaagte raho!"), dogBark, templeBell],
};

let timer = null;
let current = "afternoon";
const playOne = () => {
  if (!on()) return;
  const pool = POOLS[current] || POOLS.afternoon;
  pool[Math.floor(Math.random() * pool.length)]();
};
const schedule = () => {
  const gap = 7000 + Math.random() * 9000; // subtle, spaced out
  timer = setTimeout(() => { playOne(); schedule(); }, gap);
};

export function startGali(tod = "afternoon") { current = tod; if (!timer) schedule(); }
export function setGaliTime(tod) { current = tod; }
export function stopGali() {
  if (timer) { clearTimeout(timer); timer = null; }
  try { if (window.speechSynthesis) window.speechSynthesis.cancel(); } catch { /* ignore */ }
}
